import { useRouter } from 'next/compat/router';
import { useCallback } from 'react';

type UpdateQueryParamOptions = {
	method?: 'push' | 'replace';
	shallow?: boolean;
	scroll?: boolean;
};

export const useQueryParam = (key: string) => {
	const router = useRouter();

	const getValue = useCallback(() => {
		if (typeof window !== 'undefined') {
			const params = new URLSearchParams(window.location.search);
			return params.get(key);
		}

		if (!router?.isReady) return null;

		const value = router.query[key];
		if (Array.isArray(value)) return value[0] ?? null;
		return value ?? null;
	}, [key, router?.isReady, router?.query]);

	const buildUrl = useCallback(
		(nextValue: string | null) => {
			const rawPath =
				typeof window !== 'undefined'
					? window.location.pathname
					: router?.asPath.split('?')[0] ?? '/';
			const basePath = router?.basePath ?? '';
			const normalizedPath =
				basePath && rawPath.startsWith(basePath)
					? rawPath.slice(basePath.length) || '/'
					: rawPath;
			const search =
				typeof window !== 'undefined' ? window.location.search : '';
			const params = new URLSearchParams(search);

			if (nextValue === null) {
				params.delete(key);
			} else {
				params.set(key, nextValue);
			}

			const queryString = params.toString();
			return queryString
				? `${normalizedPath}?${queryString}`
				: normalizedPath;
		},
		[key, router?.asPath, router?.basePath]
	);

	const setValue = useCallback(
		(nextValue: string | null, options: UpdateQueryParamOptions = {}) => {
			if (!router?.isReady) return;

			const { method = 'push', shallow = true, scroll = false } = options;
			const url = buildUrl(nextValue);
			router[method](url, undefined, { shallow, scroll });
		},
		[buildUrl, router]
	);

	return { getValue, setValue, isReady: router?.isReady } as const;
};
